import { useEffect, useState } from "react";
import { Share, Plus, X } from "lucide-react";

const DISMISSED_KEY = "snaplist.installHintDismissed";
const VISIT_COUNT_KEY = "snaplist.visitCount";
// Session-scoped flag so React 18 StrictMode double-mounting (and dev-mode
// remounts) don't inflate the per-session visit count.
const SESSION_COUNTED_KEY = "snaplist.visitCountedThisSession";

/**
 * Dismissible nudge shown on iPhone/iPad Safari explaining how to install
 * SnapList to the home screen. Hidden when:
 *   - it's the user's first visit (we don't nag before they've seen the app),
 *   - the user already launched from the home screen (standalone mode),
 *   - the user has previously dismissed the hint,
 *   - the device isn't iOS (Android Chrome shows its own install banner).
 */
export default function InstallHint() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      // Bump the visit counter so the hint shows up on return visits only.
      // Guard with a sessionStorage flag so StrictMode's double-mount (and
      // single-session reloads during dev) don't inflate the count.
      const prev = Number.parseInt(window.localStorage.getItem(VISIT_COUNT_KEY) || "0", 10) || 0;
      const alreadyCountedThisSession = window.sessionStorage.getItem(SESSION_COUNTED_KEY) === "1";
      const current = alreadyCountedThisSession ? prev : prev + 1;
      if (!alreadyCountedThisSession) {
        window.localStorage.setItem(VISIT_COUNT_KEY, String(current));
        window.sessionStorage.setItem(SESSION_COUNTED_KEY, "1");
      }
      if (current < 2) return; // first visit — stay silent

      if (window.localStorage.getItem(DISMISSED_KEY) === "1") return;
      const ua = window.navigator.userAgent;
      const isIOS = /iPhone|iPod/i.test(ua) ||
        (ua.includes("Mac") && typeof document !== "undefined" && navigator.maxTouchPoints > 1);
      if (!isIOS) return;
      const standalone = window.navigator.standalone === true ||
        window.matchMedia("(display-mode: standalone)").matches;
      if (standalone) return;
      const t = setTimeout(() => setVisible(true), 1800);
      return () => clearTimeout(t);
    } catch { /* ignore */ }
  }, []);

  // Auto-fade after 20s so the hint doesn't linger while the user is drafting
  // a listing. We don't stamp DISMISSED_KEY here — if they didn't actively tap
  // "X", they'll see the hint again on their next visit.
  useEffect(() => {
    if (!visible) return;
    const t = setTimeout(() => setVisible(false), 20_000);
    return () => clearTimeout(t);
  }, [visible]);

  const dismiss = () => {
    try { window.localStorage.setItem(DISMISSED_KEY, "1"); } catch { /* ignore */ }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed inset-x-3 bottom-3 z-50 flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-lg sm:left-auto sm:right-4 sm:max-w-sm"
      role="dialog"
      aria-label="Install SnapList to home screen"
      data-testid="install-hint"
    >
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#0053DA]">
        <img src="/icons/favicon-64.png" alt="" className="h-7 w-7" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-slate-900">Install SnapList</p>
        <p className="mt-0.5 text-xs text-slate-600">
          Tap <Share className="inline h-3.5 w-3.5 -mt-0.5" aria-label="Share icon" /> then
          <span className="mx-1 inline-flex items-center gap-0.5 rounded bg-slate-100 px-1 py-0.5 font-mono text-[11px]">
            <Plus className="h-3 w-3" /> Add to Home Screen
          </span>
          for a full-screen app icon.
        </p>
      </div>
      <button
        type="button"
        onClick={dismiss}
        className="shrink-0 rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-900"
        aria-label="Dismiss install hint"
        data-testid="install-hint-dismiss"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
